import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { Flag, Ban, Loader2, Trash2, UserCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";

export default function ForumModerationPanel() {
  const [loading, setLoading] = useState(true);
  const [flagged, setFlagged] = useState({ threads: [], replies: [] });
  const [banned, setBanned] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [unbanTarget, setUnbanTarget] = useState(null);
  const { toast } = useToast();

  const load = useCallback(async () => {
    try {
      const [{ data: f }, { data: b }] = await Promise.all([
        API.get("/forum/admin/flagged"),
        API.get("/forum/admin/banned"),
      ]);
      setFlagged(f || { threads: [], replies: [] });
      setBanned(b || []);
    } catch {
      toast({ title: "Couldn't load moderation queue", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [toast]);
  useEffect(() => { load(); }, [load]);

  const deleteItem = async () => {
    if (!deleteTarget) return;
    try {
      await API.delete(`/forum/${deleteTarget.kind}/${deleteTarget.id}`);
      toast({ title: `${deleteTarget.kind === "threads" ? "Thread" : "Reply"} removed` });
      setDeleteTarget(null);
      load();
    } catch {
      toast({ title: "Couldn't delete", variant: "destructive" });
    }
  };

  const unbanUser = async () => {
    if (!unbanTarget) return;
    try {
      await API.post(`/forum/users/${unbanTarget.id}/unban`);
      toast({ title: `${unbanTarget.forum_display_name || unbanTarget.email} unbanned` });
      setUnbanTarget(null);
      load();
    } catch {
      toast({ title: "Couldn't unban", variant: "destructive" });
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const totalFlagged = flagged.threads.length + flagged.replies.length;

  return (
    <div className="space-y-4" data-testid="forum-moderation-panel">
      <div>
        <h3 className="font-display text-lg font-semibold">Forum moderation</h3>
        <p className="text-xs text-muted-foreground">Review user-reported content and manage banned members.</p>
      </div>

      <Tabs defaultValue="flagged">
        <TabsList className="w-full">
          <TabsTrigger value="flagged" className="flex-1 gap-1.5" data-testid="mod-tab-flagged">
            <Flag className="w-4 h-4" /> Flagged ({totalFlagged})
          </TabsTrigger>
          <TabsTrigger value="banned" className="flex-1 gap-1.5" data-testid="mod-tab-banned">
            <Ban className="w-4 h-4" /> Banned ({banned.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="flagged" className="space-y-3 mt-4">
          {totalFlagged === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="mod-flagged-empty">No flagged content. Nice and quiet.</p>
          ) : (
            <>
              {flagged.threads.map((t) => (
                <FlaggedCard key={t.id} post={t} kind="threads" onDelete={setDeleteTarget} onView={(p) => window.open(`/forum/${p.id}`, "_blank")} />
              ))}
              {flagged.replies.map((r) => (
                <FlaggedCard key={r.id} post={r} kind="replies" onDelete={setDeleteTarget} onView={(p) => window.open(`/forum/${p.thread_id}`, "_blank")} />
              ))}
            </>
          )}
        </TabsContent>

        <TabsContent value="banned" className="space-y-3 mt-4">
          {banned.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8" data-testid="mod-banned-empty">No banned users.</p>
          ) : banned.map((u) => (
            <div key={u.id} className="bg-card border border-border rounded-xl p-4 flex items-start gap-3" data-testid={`mod-banned-${u.id}`}>
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                <Ban className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate">{u.forum_display_name || u.email}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {u.full_name || u.email} · banned {u.forum_banned_at ? formatDistanceToNow(new Date(u.forum_banned_at), { addSuffix: true }) : ""}
                </p>
                {u.forum_banned_reason && (
                  <p className="text-xs text-muted-foreground mt-1 italic">"{u.forum_banned_reason}"</p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="rounded-full gap-1 shrink-0"
                onClick={() => setUnbanTarget(u)}
                data-testid={`mod-unban-${u.id}`}
              >
                <UserCheck className="w-3.5 h-3.5" /> Unban
              </Button>
            </div>
          ))}
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently remove this {deleteTarget?.kind === "threads" ? "thread" : "reply"}?</AlertDialogTitle>
            <AlertDialogDescription>
              This is permanent. The {deleteTarget?.kind === "threads" ? "thread and all its replies" : "reply"} will be erased from the forum — members will see no trace of it.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteItem} className="bg-red-600 hover:bg-red-700">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!unbanTarget} onOpenChange={(o) => !o && setUnbanTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unban {unbanTarget?.forum_display_name || unbanTarget?.email}?</AlertDialogTitle>
            <AlertDialogDescription>
              They'll be able to view and post in the ADHD Club Forum again. Their prior posts remain visible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={unbanUser}>Unban</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function FlaggedCard({ post, kind, onDelete, onView }) {
  return (
    <div className="bg-card border border-amber-200 rounded-xl p-4 space-y-2" data-testid={`mod-flagged-${kind}-${post.id}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="inline-flex items-center gap-1 font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
          <Flag className="w-3 h-3" /> {post.flags?.length || 0} flag{post.flags?.length === 1 ? "" : "s"}
        </span>
        <span className="text-muted-foreground">
          {kind === "threads" ? "Thread" : "Reply"} · {post.author_display_name || "?"} ·{" "}
          {post.created_date ? formatDistanceToNow(new Date(post.created_date), { addSuffix: true }) : ""}
        </span>
      </div>
      {post.title && <h4 className="font-semibold text-sm">{post.title}</h4>}
      <p className="text-sm text-muted-foreground whitespace-pre-wrap line-clamp-3">{post.body}</p>
      {post.flags?.length > 0 && (
        <div className="space-y-1 pt-2 border-t border-border/60">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Reports</p>
          {post.flags.slice(0, 3).map((f, i) => (
            <p key={`${f.reason || "flag"}-${i}`} className="text-xs text-muted-foreground italic">"{f.reason || "(no reason)"}"</p>
          ))}
        </div>
      )}
      <div className="flex gap-2 pt-1">
        <Button size="sm" variant="outline" className="flex-1 rounded-full" onClick={() => onView(post)}>
          View in forum
        </Button>
        <Button
          size="sm"
          className="flex-1 rounded-full bg-red-600 hover:bg-red-700 gap-1"
          onClick={() => onDelete({ kind, id: post.id })}
          data-testid={`mod-delete-${kind}-${post.id}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Remove
        </Button>
      </div>
    </div>
  );
}
